/**
 * The seller's mandatory mentions of an invoice (SFE spec §3 a, b, c, d, j,
 * k, l, x, and the issuer identification of CGI art. 562, 5°: legal nature,
 * legal form of a legal person, register number): which of them a company's
 * settings leave empty.
 *
 * An invoice prints these mentions from the company's settings, the §3 c
 * address and cadastral reference from its place of sale (`placeOfSale`),
 * so an issue is refused while a blocking one is missing. §3 j (bank references) is
 * reported but does not block. §3 x (ISF) identifies the certified billing
 * system and is required of a certified invoice only. The postal box
 * (`Tenant.postalBox`) prints when set and is not checked.
 *
 * A jurisdiction with the GENERIC_MINIMUM rule (#181) blocks on the name,
 * the tax identifier and the address only, a product minimum and not a
 * legal claim; the other mentions print when set.
 */
import { LEGAL_NATURES, isLegalNature } from "../constants/legal-identity.js";
import { normalizeInvoicingBankAccounts, printedBankReferences } from "./invoicing-bank-accounts.js";
// §3 c: section, ilot and parcelle in the SSSS LLL PPPP grouping, 11 digits
// separated by single spaces, matched against the whole value.
const CADASTRAL_REFERENCE_REGEX = /^\d{4} \d{3} \d{4}$/;
export const CADASTRAL_REFERENCE_MESSAGE = "Références cadastrales invalides. Le format attendu est SSSS LLL PPPP : " +
    "11 chiffres groupés 4-3-4 (section, îlot, parcelle), par exemple 0012 001 0001.";
/** True when the value is exactly one SSSS LLL PPPP cadastral reference. */
export function hasCadastralReference(value) {
    return CADASTRAL_REFERENCE_REGEX.test(value);
}
/** The settings sections the web app edits these mentions in. */
export const ISSUER_MENTION_SETTINGS = {
    ENTREPRISE: "Paramètres › Entreprise",
    FISCALITE: "Paramètres › Fiscalité (DGI)",
    FACTURATION: "Paramètres › Facturation",
    ETABLISSEMENTS: "Administration › Établissements",
};
/** API error code of an issue refused for a missing mention. */
export const ISSUER_MENTIONS_MISSING_CODE = "MENTIONS_OBLIGATOIRES_MANQUANTES";
/**
 * The §3 c address and cadastral reference of the place of sale (SFE spec
 * §3 c): those of the establishment a document is issued in when it is not
 * the company's default one, else the company's. The pair comes whole from
 * one of the two; a field the establishment leaves empty stays empty.
 */
export function placeOfSale(tenant, site) {
    if (site === null || site.isDefault) {
        return { address: tenant.address ?? null, cadastralReference: tenant.cadastralReference ?? null, site: null };
    }
    return { address: site.address, cadastralReference: site.cadastralReference, site: site.code };
}
/** The blocking mentions of the GENERIC_MINIMUM rule: name, tax identifier, address. */
function genericMinimumMissing(source, taxIdentifierLabel) {
    const { ENTREPRISE } = ISSUER_MENTION_SETTINGS;
    const missing = [];
    const add = (key, label) => missing.push({ key, clause: "", label, settings: ENTREPRISE, blocking: true });
    if (blank(source.name))
        add("name", "nom de l'entreprise");
    if (blank(source.ifu))
        add("ifu", `${taxIdentifierLabel} de l'entreprise`);
    if (blank(source.address))
        add("address", "adresse de l'entreprise");
    return missing;
}
function blank(value) {
    return value === null || value === undefined || value.trim() === "";
}
/**
 * The mentions `source` leaves missing, in §3 order. A cadastral reference
 * not in the SSSS LLL PPPP format counts as missing.
 */
export function missingIssuerMentions(source, options) {
    if (options.rule === "GENERIC_MINIMUM") {
        return genericMinimumMissing(source, options.taxIdentifierLabel ?? "Identifiant fiscal");
    }
    const { ENTREPRISE, FISCALITE, FACTURATION } = ISSUER_MENTION_SETTINGS;
    const missing = [];
    const add = (key, clause, label, settings, blocking = true) => {
        missing.push({ key, clause, label, settings, blocking });
    };
    if (blank(source.name))
        add("name", "§3 a", "nom de l'entreprise", ENTREPRISE);
    // CGI art. 562, 5°: the nature decides what identifies the issuer, the
    // names of a natural person or the legal form and company name of a legal
    // person. A value outside `LEGAL_NATURES` counts as missing.
    if (!isLegalNature(source.legalNature)) {
        add("legalNature", "CGI art. 562, 5°", "nature de l'émetteur : personne physique, personne morale ou association", ENTREPRISE);
    }
    else if (source.legalNature === LEGAL_NATURES.PERSONNE_MORALE.code &&
        blank(source.legalForm)) {
        add("legalForm", "CGI art. 562, 5°", "forme juridique de la personne morale", ENTREPRISE);
    }
    if (blank(source.ifu))
        add("ifu", "§3 b", "IFU de l'entreprise", ENTREPRISE);
    // CGI art. 562, 5° asks every issuer, whatever its legal nature, for its
    // number in the RCCM, the civil companies and trades register or the crafts
    // register; `Tenant.rccm` holds whichever applies, and an association's
    // declaration receipt.
    if (blank(source.rccm)) {
        add("rccm", "CGI art. 562, 5°", source.legalNature === LEGAL_NATURES.ASSOCIATION.code
            ? "récépissé de déclaration de l'association"
            : "numéro RCCM ou d'immatriculation au registre des métiers", ENTREPRISE);
    }
    // §3 c: an establishment's pair is entered on the establishment, the
    // company's in its settings.
    const site = options.placeOfSaleSite ?? null;
    const ofPlace = site === null ? "du lieu de vente" : `de l'établissement ${site}, lieu de vente`;
    if (blank(source.address)) {
        add("address", "§3 c", `adresse ${ofPlace}`, site === null ? ENTREPRISE : ISSUER_MENTION_SETTINGS.ETABLISSEMENTS);
    }
    const cadastral = source.cadastralReference?.trim() ?? "";
    if (!hasCadastralReference(cadastral)) {
        add("cadastralReference", "§3 c", cadastral === ""
            ? `références cadastrales ${ofPlace}`
            : `références cadastrales ${ofPlace} au format SSSS LLL PPPP`, site === null ? FISCALITE : ISSUER_MENTION_SETTINGS.ETABLISSEMENTS);
    }
    if (blank(source.phone))
        add("phone", "§3 d", "téléphone", ENTREPRISE);
    if (blank(source.email))
        add("email", "§3 d", "adresse e-mail", ENTREPRISE);
    if (printedBankReferences(normalizeInvoicingBankAccounts(source.bankReferences)).length === 0) {
        add("bankReferences", "§3 j", "références bancaires", FACTURATION, false);
    }
    if (blank(source.taxRegime))
        add("taxRegime", "§3 k", "régime d'imposition", FISCALITE);
    if (blank(source.taxOffice)) {
        add("taxOffice", "§3 l", "service des impôts de rattachement", FISCALITE);
    }
    if (options.certified && blank(source.isf)) {
        add("isf", "§3 x", "ISF (identifiant du système de facturation)", FISCALITE);
    }
    return missing;
}
/**
 * The French refusal of an issue for the blocking mentions in `missing`,
 * grouped by settings section; null when none of them blocks.
 */
export function issuerMentionsRefusal(missing, rule = "BF_SFE") {
    const blocking = missing.filter((mention) => mention.blocking);
    if (blocking.length === 0)
        return null;
    const sections = new Map();
    for (const mention of blocking) {
        const labels = sections.get(mention.settings) ?? [];
        labels.push(mention.clause === "" ? mention.label : `${mention.label} (${mention.clause})`);
        sections.set(mention.settings, labels);
    }
    const parts = [...sections].map(([settings, labels]) => `À compléter dans ${settings} : ${labels.join(", ")}.`);
    if (rule === "GENERIC_MINIMUM") {
        return `Émission impossible : des mentions de l'entreprise manquent sur la facture. ${parts.join(" ")}`;
    }
    return ("Émission impossible : des mentions obligatoires de l'entreprise manquent " +
        `sur la facture (SFE §3, CGI art. 562, 5°). ${parts.join(" ")}`);
}
//# sourceMappingURL=issuer-mentions.js.map